import type { FC, ReactNode, RefObject } from 'react';

import { Recaptcha } from '@eneco/components/Recaptcha/Recaptcha';
import type { RecaptchaRef } from '@eneco/components/Recaptcha/Recaptcha';
import { RecaptchaForm } from '@eneco/components/RecaptchaForm/RecaptchaForm';
import RichText from '@eneco/components/RichText/RichText';
import { Box, Button, InputText, Stack, Stretch, Text, TextLink } from '@eneco/sparky';
import { FormProvider } from 'react-hook-form';
import type { FieldValues, Path, SubmitHandler, UseFormReturn } from 'react-hook-form';

import type { NewsletterEmailField } from '../newsletter-form-integrated-with-revolution-content.types';

type RevolutionNewsletterValues = FieldValues & { emailaddress1: string; recaptchaToken: string };

interface RevolutionNewsletterFormProps<T extends RevolutionNewsletterValues> {
  methods: UseFormReturn<T>;
  onSubmit: SubmitHandler<T>;
  recaptchaRef: RefObject<RecaptchaRef | null>;
  emailField?: NewsletterEmailField;
  emailError?: string;
  nextButtonText?: string;
  disclaimer?: string;
  /** Rendered between the email field and the submit button: hidden CRM fields or a `jss-form` slot. */
  children?: ReactNode;
}

/**
 * The email field, submit button, disclaimer and reCAPTCHA notice shared by the two Revolution CRM
 * newsletter blocks (`NewsletterFormIntegratedWithRevolution`, `RevolutionRegisterToNewsletterForm`).
 * Their legacy components render this markup identically; each block keeps its own submit and
 * error-message logic.
 */
export const RevolutionNewsletterForm = <T extends RevolutionNewsletterValues>({
  methods,
  onSubmit,
  recaptchaRef,
  emailField,
  emailError,
  nextButtonText,
  disclaimer,
  children,
}: RevolutionNewsletterFormProps<T>) => (
  <FormProvider {...methods}>
    <RecaptchaForm onSubmit={onSubmit} recaptchaRef={recaptchaRef}>
      <Stack gap="6">
        <Stack
          direction={{ initial: 'column', md: 'row' }}
          gap="6"
          alignY="justify"
          alignX={{ initial: undefined, md: 'justify' }}>
          <Stack.Item grow>
            <InputText
              {...methods.register('emailaddress1' as Path<T>)}
              label={emailField?.label ?? 'Email'}
              placeholder={emailField?.placeholder ?? 'mijn@email.nl'}
              hint={emailField?.hint}
              error={emailError}
            />
          </Stack.Item>

          {children}

          <Box paddingTop={{ md: '7' }}>
            <Stretch width={{ initial: true, md: false }} height={false}>
              <Button size="compact" type="submit">
                {nextButtonText}
              </Button>
            </Stretch>
          </Box>
        </Stack>
        <Text size="BodyS">
          <RichText html={disclaimer} />
        </Text>
        <Text size="BodyS">
          <Recaptcha hasError={!!methods.formState.errors.recaptchaToken} ref={recaptchaRef} />
        </Text>
      </Stack>
    </RecaptchaForm>
  </FormProvider>
);

/** The "back" link shown after a submit: fades out, resets the mutation, and fades the form back in. */
export const RevolutionNewsletterBackLink: FC<{
  text?: string;
  reset: () => void;
  setTransitioning: (isTransitioning: boolean) => void;
}> = ({ text, reset, setTransitioning }) => (
  <TextLink
    type="button"
    onClick={() => {
      setTransitioning(true);
      setTimeout(() => {
        reset();
        setTransitioning(false);
      }, 500);
    }}
    emphasis="high">
    {text}
  </TextLink>
);
